import { useEffect, useState } from 'react';
import './journeydetail.css';
import JourneyScene from './JourneyScene';
import { CATEGORY_STYLES, journeyStats, getJourney as getFallbackJourney } from './journeysData';
import { getJourneys } from '../../services/journeyService';
import { getActiveShopsForJourney } from '../../services/shopService';
import { mapJourneyForUi } from './journeyAdapter';

function ArrowLeftIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M19 12H5M11 6l-6 6 6 6" stroke="#3d2f7a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path d="M9 5l7 7-7 7" stroke="#c4bfe0" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
      <path d="M5 13l5 5L19 7" stroke="#ffffff" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function JourneyDetail({ journeyId, onBack, onSelectShop, userMissions }) {
  // null while the Laravel request is in flight or no journey is selected
  // yet; an object once a journey has loaded (fallback included).
  const [journey, setJourney] = useState(null);
  const [isLoading, setIsLoading] = useState(journeyId != null);

  useEffect(() => {
    if (journeyId == null) {
      setJourney(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    Promise.all([getJourneys(), getActiveShopsForJourney(journeyId)])
      .then(([journeys, shops]) => {
        if (cancelled) return;
        const found = journeys.find((j) => j.id === journeyId);
        setJourney(found ? mapJourneyForUi(found, shops, userMissions) : null);
      })
      .catch((err) => {
        // Genuine-API-failure fallback ONLY. Never used when Laravel
        // successfully responds, even if the journey isn't found in it.
        console.error('JourneyDetail: failed to load journey from Laravel, using fallback data.', err);
        if (!cancelled) setJourney(getFallbackJourney(journeyId));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // userMissions is included so a load that completes after this effect's
    // initial run (Step 5H's GET /user-missions arriving shortly after auth)
    // re-maps the same journey/shops with real visited state, instead of
    // requiring the user to re-select the journey to see it.
  }, [journeyId, userMissions]);

  if (isLoading || !journey) {
    return (
      <div className="journeydetail-screen">
        <div className="journeydetail-frame">
          <div className="journeydetail-top">
            <button type="button" className="journeydetail-back-btn" aria-label="Go back" onClick={onBack}>
              <ArrowLeftIcon />
            </button>
            <h1 className="journeydetail-title">My Journeys</h1>
            <span className="journeydetail-top-spacer" />
          </div>
          <div className="journeydetail-status">
            {isLoading ? 'Loading journey…' : 'Journey not found.'}
          </div>
        </div>
      </div>
    );
  }

  const { visited, total, pct } = journeyStats(journey);
  const isCompleted = pct === 100;

  return (
    <div className="journeydetail-screen">
      <div className="journeydetail-frame">
        <div className="journeydetail-top">
          <button type="button" className="journeydetail-back-btn" aria-label="Go back" onClick={onBack}>
            <ArrowLeftIcon />
          </button>
          <h1 className="journeydetail-title">{journey.title}</h1>
          <span className="journeydetail-top-spacer" />
        </div>

        <div className="journeydetail-content">
          <div className="journeydetail-hero">
            <div className="journeydetail-hero-scene">
              <JourneyScene theme={journey.theme} />
            </div>
            <div className="journeydetail-hero-scrim" />
            <div className="journeydetail-hero-text">
              <h2 className="journeydetail-hero-title">{journey.title}</h2>
              <p className="journeydetail-hero-desc">{journey.subtitle}</p>
            </div>
          </div>

          <div className="journeydetail-progress-card">
            <div className="journeydetail-progress-row">
              <span className="journeydetail-progress-count">
                <strong>
                  {visited}/{total}
                </strong>{' '}
                Shops Visited
              </span>
              <span className="journeydetail-progress-pct">{pct}%</span>
            </div>
            <div className="journeydetail-progress-track">
              <div className="journeydetail-progress-fill" style={{ width: `${pct}%` }} />
            </div>
          </div>

          <h3 className="journeydetail-section-title">Shop List</h3>

          <div className="journeydetail-shop-list">
            {journey.shops.length === 0 ? (
              <div className="journeydetail-status">No shops in this journey yet.</div>
            ) : (
              journey.shops.map((shop) => {
                const style = CATEGORY_STYLES[shop.style] || CATEGORY_STYLES.cafe;
                const rowLocked = shop.visited || isCompleted;
                const RowTag = rowLocked ? 'div' : 'button';
                return (
                  <RowTag
                    type={rowLocked ? undefined : 'button'}
                    className="journeydetail-shop-row"
                    key={shop.id}
                    onClick={rowLocked ? undefined : () => onSelectShop?.(shop)}
                  >
                    <span className="journeydetail-shop-icon" style={{ background: style.bg }}>
                      {style.icon}
                    </span>
                    <span className="journeydetail-shop-info">
                      <span className="journeydetail-shop-name">{shop.name}</span>
                      <span className="journeydetail-shop-category">{shop.category}</span>
                    </span>
                    <span
                      className={
                        'journeydetail-shop-badge' + (shop.visited ? ' journeydetail-shop-badge-visited' : '')
                      }
                    >
                      {shop.visited ? <CheckIcon /> : null}
                      {shop.visited ? 'Completed' : 'Pending'}
                    </span>
                    {!rowLocked && (
                      <span className="journeydetail-shop-chevron">
                        <ChevronRightIcon />
                      </span>
                    )}
                  </RowTag>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
